import { jobs, companies, type Job, type Company } from '../data/mockData';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

export type SearchResult = {
  id: string;
  title: string;
  subtitle?: string;
  type: 'job' | 'company' | 'page';
  url: string;
  score?: number;
  rank?: number;
};

const fallbackPages: SearchResult[] = [
  { id: 'pricing', title: 'Pricing & Plans', type: 'page', url: '/pricing' },
  { id: 'contact', title: 'Contact Lighthouse', type: 'page', url: '/contact' },
  { id: 'returner-hub', title: 'Returner Hub', type: 'page', url: '/returner-hub' },
];

const slugify = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const normaliseJobResult = (job: Job): SearchResult => ({
  id: String(job.id),
  title: job.title,
  subtitle: job.location,
  type: 'job',
  url: `/jobs/${(job as { slug?: string }).slug ?? job.id}`,
});

const normaliseCompanyResult = (company: Company): SearchResult => ({
  id: `company-${company.id}`,
  title: company.name,
  subtitle: company.industry,
  type: 'company',
  url: `/company/${slugify(company.name)}`,
});

const computeScore = (query: string, title: string, subtitle?: string) => {
  const lowered = query.toLowerCase();
  let score = 0;
  if (title.toLowerCase().includes(lowered)) {
    score += 10;
  }
  if (subtitle?.toLowerCase().includes(lowered)) {
    score += 4;
  }
  if (title.toLowerCase().startsWith(lowered)) {
    score += 5;
  }
  return score;
};

const annotateRank = (items: SearchResult[]): SearchResult[] =>
  items.map((item, index) => ({ ...item, rank: item.rank ?? index + 1 }));

const buildFallback = (query: string): SearchResult[] => {
  const jobResults = jobs
    .map((job) => {
      const result = normaliseJobResult(job);
      return { ...result, score: computeScore(query, result.title, result.subtitle) };
    })
    .filter((result) => (result.score ?? 0) > 0)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
    .slice(0, 5);
  const companyResults = companies
    .map((company) => {
      const result = normaliseCompanyResult(company);
      return { ...result, score: computeScore(query, result.title, result.subtitle) };
    })
    .filter((result) => (result.score ?? 0) > 0)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
    .slice(0, 5);
  const pageResults = fallbackPages
    .map((page) => ({ ...page, score: computeScore(query, page.title) }))
    .filter((page) => page.score && page.score > 0);
  return annotateRank([...jobResults, ...companyResults, ...pageResults]);
};

export async function searchEntities(query: string): Promise<SearchResult[]> {
  if (!query.trim()) return [];
  if (!API_BASE_URL) {
    return buildFallback(query);
  }

  try {
    const response = await fetch(`${API_BASE_URL}/v1/search?q=${encodeURIComponent(query)}`);
    if (!response.ok) {
      throw new Error(`Search failed (${response.status})`);
    }
    const payload = (await response.json()) as { results?: SearchResult[] };
    if (Array.isArray(payload?.results)) {
      return annotateRank(payload.results as SearchResult[]);
    }
    return buildFallback(query);
  } catch (error) {
    console.warn('[search] falling back to local ranking', error);
    return buildFallback(query);
  }
}

export async function logSearchClick(event: {
  resultId: string;
  url: string;
  type?: string;
  rank?: number;
  score?: number;
  query?: string;
  title?: string;
  subtitle?: string;
}) {
  if (!API_BASE_URL) {
    console.info('[search-click]', event);
    return;
  }
  try {
    await fetch(`${API_BASE_URL}/v1/search/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'click',
        query: event.query,
        resultId: event.resultId,
        resultType: event.type,
        resultTitle: event.title,
        rank: event.rank,
        score: event.score,
        url: event.url,
        metadata: { subtitle: event.subtitle },
      }),
    });
  } catch (error) {
    console.warn('[search] failed to log click', error);
  }
}
